import { useEffect, useRef } from "react";

import { countExportedOutcomes } from "./outcome-export-file-utils";

import {
  ExportedOutcome,
  ExportedOutcomeSubgroup,
  OutcomeExportFile,
} from "api/api-server-outcome-transfer";
import { OutcomeCount } from "components/materials/home/admin/outcomes/OutcomeCount";
import { t } from "i18n/i18n";
import { Card } from "mds/components/Card";
import { ListItem } from "mds/components/ListItem";
import { ChevronRightIcon } from "mds/icons";

function ExportedOutcomeList({ outcomes }: { outcomes: ExportedOutcome[] }) {
  return (
    <ul>
      {outcomes.map((outcome) => (
        <ListItem className="w-full" key={outcome.title} label={outcome.title}>
          <div className="body-s flex w-full items-center">
            #<span className="min-w-0 flex-1 truncate px-2">{outcome.title}</span>
          </div>
        </ListItem>
      ))}
    </ul>
  );
}

function OutcomeSubgroupPreview({ outcomeSubgroup }: { outcomeSubgroup: ExportedOutcomeSubgroup }) {
  return (
    <li>
      <h5 className="mb-1 ml-1 mt-3 font-medium">{outcomeSubgroup.title}</h5>
      <ExportedOutcomeList outcomes={outcomeSubgroup.outcomes} />
    </li>
  );
}

export function OutcomeTreeImportPreview({
  outcomeExportFile,
}: {
  outcomeExportFile: OutcomeExportFile;
}) {
  const previewHeadingRef = useRef<HTMLHeadingElement>(null);
  const totalOutcomeCount = countExportedOutcomes(outcomeExportFile);

  useEffect(() => previewHeadingRef.current?.focus(), []);

  return (
    <section aria-labelledby="outcome-tree-import-preview-title" className="mb-4">
      <div className="mb-2 flex items-center justify-between">
        <h3
          className="h3 my-0 text-black-tint-40"
          id="outcome-tree-import-preview-title"
          ref={previewHeadingRef}
          tabIndex={-1}
        >
          {t("outcome_transfer.outcomes_to_import")}
        </h3>
        <OutcomeCount count={totalOutcomeCount} />
      </div>
      <div className="space-y-3 rounded-lg bg-black-tint-99 p-3">
        {outcomeExportFile.groups.map((outcomeGroup) => {
          const outcomeCount = countExportedOutcomes({ groups: [outcomeGroup] });
          return (
            <Card key={outcomeGroup.title} small>
              {outcomeCount === 0 ? (
                <div className="flex min-h-11 items-center gap-2">
                  <h4 className="min-w-0 flex-1 break-words font-medium">{outcomeGroup.title}</h4>
                  <OutcomeCount count={0} />
                </div>
              ) : (
                <details className="group" open>
                  <summary className="flex min-h-11 cursor-pointer list-none items-center gap-2 rounded-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue [&::-webkit-details-marker]:hidden">
                    <h4 className="min-w-0 flex-1 break-words font-medium">{outcomeGroup.title}</h4>
                    <OutcomeCount count={outcomeCount} />
                    <ChevronRightIcon className="size-4 shrink-0 text-black-tint-40 transition-transform group-open:rotate-90" />
                  </summary>
                  <div className="mt-2">
                    <ExportedOutcomeList outcomes={outcomeGroup.outcomes} />
                    <ul>
                      {outcomeGroup.subgroups.map((outcomeSubgroup) => (
                        <OutcomeSubgroupPreview
                          key={outcomeSubgroup.title}
                          outcomeSubgroup={outcomeSubgroup}
                        />
                      ))}
                    </ul>
                  </div>
                </details>
              )}
            </Card>
          );
        })}
      </div>
    </section>
  );
}
